import React, { useEffect, useState } from 'react'
import { Button, Form, Modal, Table, Spinner } from 'react-bootstrap'
import { useGetTagsQuery } from '../../../../services/tagApi'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { selectSelectedProjectId } from '../../../../store/slices/projectSlice'
import { useGetProjectQuery } from '../../../../services/projectsApi'
import PillsClassification from '../../../PillsClassification'
import { FaPlus } from 'react-icons/fa'
import styled from 'styled-components'
import { setFilterTags } from '../../../../store/slices/projectSlice'
import type { Tag } from '../../../../types/models/tag'

const StyledTable = styled(Table)`
  color: #e0e0e0;
  margin-bottom: 0;
  border-collapse: separate;
  border-spacing: 0;

  th {
    background: #1a1a1a;
    border: none;
    padding: 1rem 1.25rem;
    font-weight: 600;
    font-size: 0.9rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #8c8c8c;
    cursor: pointer;
    transition: color 0.2s ease;
    white-space: nowrap;

    &:hover {
      color: #fff;
    }
  }

  td {
    background: #2a2a2a;
    padding: 1rem 1.25rem;
    border: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    vertical-align: middle;
    font-size: 0.95rem;
    color: #e0e0e0;

    .tag-name {
      font-weight: 500;
      color: #fff;
    }

    .color-preview {
      display: inline-block;
      width: 20px;
      height: 20px;
      border-radius: 4px;
      margin-right: 0.75rem;
      border: 2px solid rgba(255, 255, 255, 0.1);
      vertical-align: middle;
    }

    .hex-color {
      color: #8c8c8c;
      font-family: monospace;
      font-size: 0.9rem;
    }
  }

  tbody tr {
    background: #2a2a2a;
    transition: background-color 0.2s ease;
    cursor: pointer;

    &:hover {
      background-color: #333;
      td {
        background-color: #333;
      }
    }
  }

  .form-check {
    margin: 0;

    .form-check-input {
      background-color: #2b3035;
      border-color: #495057;
      cursor: pointer;

      &:checked {
        background-color: #0d6efd;
        border-color: #0d6efd;
      }

      &:focus {
        box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
      }
    }
  }
`

const StyledModalBody = styled(Modal.Body)`
  background: #2a2a2a;
  padding: 1rem;
  border-radius: 0 0 8px 8px;
  max-height: 50vh;
  overflow-y: auto;

  .form-control {
    background: #1a1a1a;
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: #fff;
    padding: 0.5rem 0.75rem;
    font-size: 0.95rem;

    &:focus {
      border-color: rgba(13, 110, 253, 0.5);
      box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.15);
    }

    &::placeholder {
      color: #6c757d;
    }
  }

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #1a1a1a;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #4a4a4a;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #5a5a5a;
  }
`

interface SelectedTagsCardProps {
  selectedTagIds: string[]
  onTagSelectionChange: (tagIds: string[]) => void
  disabled?: boolean
}

export const SelectedTagsCard: React.FC<SelectedTagsCardProps> = ({
  selectedTagIds,
  onTagSelectionChange,
  disabled = false,
}) => {
  const dispatch = useAppDispatch()
  const [showTagModal, setShowTagModal] = useState(false)
  const [tagFilter, setTagFilter] = useState('')

  // Redux selectors
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })

  // API hooks
  const { data: existingTagsData, isLoading: isTagsLoading } = useGetTagsQuery(
    {
      project_id: selectedProjectId || '',
      dataset_id: project?.datasetid || '',
    },
    { skip: !selectedProjectId || !project?.datasetid }
  )

  // Filtered tags
  const filteredTags = React.useMemo(() => {
    if (!existingTagsData?.tags) return []
    return existingTagsData.tags.filter((tag) =>
      tag.name.toLowerCase().includes(tagFilter.toLowerCase())
    )
  }, [existingTagsData?.tags, tagFilter])

  const handleTagSelection = (tagId: string) => {
    if (tagId === 'all') {
      onTagSelectionChange(filteredTags.map((tag) => tag.id))
    } else if (tagId === 'none') {
      onTagSelectionChange([])
    } else {
      onTagSelectionChange(
        selectedTagIds?.includes(tagId)
          ? selectedTagIds?.filter((id) => id !== tagId)
          : [...selectedTagIds, tagId]
      )
    }
  }

  // Sync selected tags into Redux (projectSlice.filterTags)
  useEffect(() => {
    const allTags: Tag[] | undefined = existingTagsData?.tags
    if (!allTags) return
    const selectedTags = allTags.filter((t) => selectedTagIds?.includes(t.id))
    dispatch(setFilterTags(selectedTags))
  }, [dispatch, existingTagsData?.tags, selectedTagIds])

  if (!selectedTagIds?.length) {
    return (
      <>
        {/* Removed until selected tags are actually used when training a model
        <Button
          variant="outline-light"
          onClick={() => !disabled && setShowTagModal(true)}
          className="select-dataset-btn"
          style={{
            borderStyle: 'dashed',
            borderWidth: '2px',
            borderColor: '#3498db',
            backgroundColor: 'transparent',
            width: '100%',
            height: '60px',
            cursor: disabled ? 'default' : 'pointer',
            opacity: disabled ? 0.7 : 1,
          }}
          disabled={disabled}
        >
          <FaPlus className="me-2" />
          Select Tags
        </Button> */}
        <Modal
          show={showTagModal}
          onHide={() => setShowTagModal(false)}
          centered
          size="lg"
          contentClassName="bg-dark"
        >
          <Modal.Header closeButton className="border-secondary bg-dark text-white">
            <Modal.Title>Select Tags</Modal.Title>
          </Modal.Header>
          <StyledModalBody>
            <Form.Group className="mb-3">
              <Form.Control
                type="text"
                placeholder="Search tags..."
                value={tagFilter}
                onChange={(e) => setTagFilter(e.target.value)}
              />
            </Form.Group>

            <StyledTable hover responsive>
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>
                    <Form.Check
                      type="checkbox"
                      onChange={(e) => {
                        if (e.target.checked) {
                          handleTagSelection('all')
                        } else {
                          handleTagSelection('none')
                        }
                      }}
                      checked={
                        selectedTagIds?.length === filteredTags.length && filteredTags.length > 0
                      }
                    />
                  </th>
                  <th>Tag Name</th>
                  <th>Color</th>
                </tr>
              </thead>

              <tbody>
                {isTagsLoading ? (
                  <tr>
                    <td colSpan={3} className="text-center py-4">
                      <Spinner animation="border" variant="primary" size="sm" />
                    </td>
                  </tr>
                ) : filteredTags.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="text-center py-4 text-muted">
                      {tagFilter ? 'No tags match your search' : 'No tags available'}
                    </td>
                  </tr>
                ) : (
                  filteredTags.map((tag) => (
                    <tr
                      key={tag.id}
                      onClick={() => handleTagSelection(tag.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td onClick={(e) => e.stopPropagation()}>
                        <Form.Check
                          type="checkbox"
                          checked={selectedTagIds?.includes(tag.id)}
                          onChange={() => handleTagSelection(tag.id)}
                        />
                      </td>
                      <td>
                        <span className="tag-name">{tag.name}</span>
                      </td>
                      <td>
                        <span
                          className="color-preview"
                          style={{
                            backgroundColor: `#${tag.color.toString(16).padStart(6, '0')}`,
                          }}
                        />
                        <span className="hex-color">
                          #{tag.color.toString(16).padStart(6, '0')}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </StyledTable>
          </StyledModalBody>
          <Modal.Footer className="border-secondary bg-dark">
            <button className="btn btn-secondary" onClick={() => setShowTagModal(false)}>
              Close
            </button>
          </Modal.Footer>
        </Modal>
      </>
    )
  }

  return (
    <>
      <div className="d-flex flex-column">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <h6 className="text-white mb-0">Selected Tags</h6>
          {!disabled && (
            <Button
              variant="outline-light"
              size="sm"
              onClick={() => setShowTagModal(true)}
              style={{ minWidth: 'auto', padding: '0.25rem 0.5rem' }}
            >
              <FaPlus size={14} />
            </Button>
          )}
        </div>
        <div
          className="d-flex align-items-center"
          style={{ cursor: disabled ? 'default' : 'pointer' }}
        >
          <PillsClassification tagIds={selectedTagIds} maxTags={3} />
        </div>
      </div>
      <Modal
        show={showTagModal}
        onHide={() => setShowTagModal(false)}
        centered
        size="lg"
        contentClassName="bg-dark"
      >
        <Modal.Header closeButton className="border-secondary bg-dark text-white">
          <Modal.Title>Select Tags</Modal.Title>
        </Modal.Header>
        <StyledModalBody>
          <Form.Group className="mb-3">
            <Form.Control
              type="text"
              placeholder="Search tags..."
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
            />
          </Form.Group>

          <StyledTable hover responsive>
            <thead>
              <tr>
                <th style={{ width: '40px' }}>
                  <Form.Check
                    type="checkbox"
                    onChange={(e) => {
                      if (e.target.checked) {
                        handleTagSelection('all')
                      } else {
                        handleTagSelection('none')
                      }
                    }}
                    checked={
                      selectedTagIds?.length === filteredTags.length && filteredTags.length > 0
                    }
                  />
                </th>
                <th>Tag Name</th>
                <th>Color</th>
              </tr>
            </thead>

            <tbody>
              {isTagsLoading ? (
                <tr>
                  <td colSpan={3} className="text-center py-4">
                    <Spinner animation="border" variant="primary" size="sm" />
                  </td>
                </tr>
              ) : filteredTags.length === 0 ? (
                <tr>
                  <td colSpan={3} className="text-center py-4 text-muted">
                    {tagFilter ? 'No tags match your search' : 'No tags available'}
                  </td>
                </tr>
              ) : (
                filteredTags.map((tag) => (
                  <tr
                    key={tag.id}
                    onClick={() => handleTagSelection(tag.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <Form.Check
                        type="checkbox"
                        checked={selectedTagIds?.includes(tag.id)}
                        onChange={() => handleTagSelection(tag.id)}
                      />
                    </td>
                    <td>
                      <span className="tag-name">{tag.name}</span>
                    </td>
                    <td>
                      <span
                        className="color-preview"
                        style={{
                          backgroundColor: `#${tag.color.toString(16).padStart(6, '0')}`,
                        }}
                      />
                      <span className="hex-color">#{tag.color.toString(16).padStart(6, '0')}</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </StyledTable>
        </StyledModalBody>
        <Modal.Footer className="border-secondary bg-dark">
          <button className="btn btn-secondary" onClick={() => setShowTagModal(false)}>
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
